import { useEffect, useState } from "react";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Sparkles, Zap, TrendingDown, Calculator, Info } from "lucide-react";

export default function HelperPro() {
  const [cfg, setCfg] = useState(null);
  const [earnings, setEarnings] = useState(1000);
  const [rate, setRate] = useState(30);
  const [starting, setStarting] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    api.get("/config/business").then(({ data }) => setCfg(data));
    api.get("/me/earnings").then(({ data }) => setStatus(data)).catch(() => {});
  }, []);

  if (!cfg) return <div className="min-h-screen"><Navbar /><div className="p-10 text-center text-muted-foreground">Loading…</div></div>;

  const basicPct = cfg.basic_immediate_help_platform_fee;
  const proPct = cfg.pro_immediate_help_platform_fee;
  const monthly = cfg.helper_pro_monthly_price;
  const annual = cfg.helper_pro_annual_price;
  const basicFees = +(earnings * basicPct).toFixed(2);
  const proFees = +(earnings * proPct).toFixed(2);
  const savings = +(basicFees - proFees).toFixed(2);
  const netAnnual = +(savings - annual).toFixed(2);
  const breakEven = +(annual / (basicPct - proPct)).toFixed(2);

  const rateFeeBasic = +(rate * basicPct).toFixed(2);
  const rateFeePro = +(rate * proPct).toFixed(2);

  const start = async (period) => {
    setStarting(true);
    try {
      const { data } = await api.post("/payments/helper-pro/checkout", {
        period,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Helper Pro checkout");
      setStarting(false);
    }
  };

  return (
    <div className="min-h-screen warm-grain">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <div className="text-xs font-mono-label text-[color:hsl(var(--amber))]">Helper economy</div>
        <h1 className="mt-2 font-display font-extrabold text-3xl sm:text-4xl">CLING Helper Pro</h1>
        <p className="mt-3 text-muted-foreground max-w-2xl">
          Community Help stays free. Helper Pro is optional — it lowers your Immediate Help platform fee and unlocks helper tools. Pro never buys ranking.
        </p>

        {status?.plan === "pro" && (
          <div className="mt-4 border border-emerald-500/40 bg-emerald-500/5 rounded-2xl p-4 text-sm" data-testid="pro-active-banner">
            <b>Helper Pro is active.</b> Your Immediate Help fee is now {(proPct*100).toFixed(0)}%. Manage your subscription from your Stripe billing portal.
          </div>
        )}
        {status?.plan !== "pro" && status?.plan_pending?.target === "pro" && (
          <div className="mt-4 border border-[color:hsl(var(--amber))]/40 bg-[color:hsl(var(--amber))]/5 rounded-2xl p-4 text-sm" data-testid="pro-pending-banner">
            <b>Helper Pro payment pending.</b> Complete Stripe checkout to activate your reduced fee.
          </div>
        )}

        <div className="mt-8 grid md:grid-cols-2 gap-4">
          <PlanCard
            title="Helper Basic"
            price="$0"
            per=""
            feePct={basicPct}
            perks={["Full helper profile", "Community Help", "Reputation & Kudos", "Immediate Help eligibility"]}
            testId="plan-basic"
          />
          <PlanCard
            title="Helper Pro"
            price={`$${monthly.toFixed(2)}`}
            per="/month · or $99/year"
            feePct={proPct}
            perks={["Reduced Immediate Help fee", "Earnings analytics", "Scheduling tools", "Advanced availability", "Repeat-user tools"]}
            highlight
            testId="plan-pro"
            action={
              <div className="mt-4 flex gap-2">
                <Button className="btn-amber flex-1 rounded-full" disabled={starting} onClick={() => start("monthly")} data-testid="start-pro-monthly">
                  {starting ? "Redirecting…" : "Subscribe monthly"}
                </Button>
                <Button variant="outline" className="flex-1 rounded-full" disabled={starting} onClick={() => start("annual")} data-testid="start-pro-annual">
                  {starting ? "Redirecting…" : "Subscribe annual"}
                </Button>
              </div>
            }
            note="Powered by Stripe. Test mode — use card 4242 4242 4242 4242, any future expiry, any CVC."
          />
        </div>

        <div className="mt-8 border rounded-2xl bg-card p-5" data-testid="earnings-calc">
          <div className="flex items-center gap-2 text-sm font-mono-label text-[color:hsl(var(--teal))]"><Calculator className="w-4 h-4" /> Estimated annual savings</div>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <label className="text-sm text-muted-foreground">Your yearly Immediate Help earnings ($)</label>
            <Input type="number" min="0" step="100" value={earnings} onChange={(e) => setEarnings(Math.max(0, +e.target.value || 0))} className="w-32" data-testid="earnings-input" />
          </div>
          <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
            <div className="rounded-xl bg-secondary p-3"><div className="text-xs text-muted-foreground">Basic ({(basicPct*100).toFixed(0)}%)</div><div className="mt-1 font-display font-bold text-xl">${basicFees.toLocaleString()}</div><div className="text-xs text-muted-foreground">platform fees</div></div>
            <div className="rounded-xl bg-[color:hsl(var(--amber))]/10 border border-[color:hsl(var(--amber))]/30 p-3"><div className="text-xs text-[color:hsl(var(--amber))] font-mono-label">Pro ({(proPct*100).toFixed(0)}%)</div><div className="mt-1 font-display font-bold text-xl">${proFees.toLocaleString()}</div><div className="text-xs text-muted-foreground">platform fees</div></div>
          </div>
          <div className="mt-4 rounded-xl border p-3 flex items-start gap-2">
            <TrendingDown className="w-4 h-4 text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0" />
            <div className="text-sm">
              You'd save <b>${savings.toLocaleString()}</b> in platform fees. After the ${annual.toFixed(2)}/yr Pro subscription, net savings is <b data-testid="net-annual">{netAnnual >= 0 ? `$${netAnnual.toFixed(2)}` : `–$${Math.abs(netAnnual).toFixed(2)}`}</b>.
              <div className="text-xs text-muted-foreground mt-1">Break-even at ~<b>${breakEven.toLocaleString()}</b>/yr of Immediate Help earnings.</div>
            </div>
          </div>
          <div className="mt-3 text-[11px] text-muted-foreground inline-flex items-center gap-1"><Info className="w-3 h-3" /> Estimates only. CLING does not promise income. Recomputes from current config.</div>
        </div>

        <div className="mt-8 border rounded-2xl bg-card p-5">
          <div className="flex items-center gap-2 text-sm font-mono-label text-[color:hsl(var(--teal))]"><Zap className="w-4 h-4" /> Per-session earnings preview</div>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <label className="text-sm text-muted-foreground">Your Immediate Help rate ($)</label>
            <Input type="number" min="1" step="1" value={rate} onChange={(e) => setRate(Math.max(0, +e.target.value || 0))} className="w-24" data-testid="rate-input" />
          </div>
          <div className="mt-3 grid sm:grid-cols-2 gap-3 text-sm">
            <div className="rounded-xl border p-3">
              <div className="text-xs font-mono-label text-muted-foreground">Basic</div>
              <div className="mt-1 flex justify-between"><span>Your rate</span><b>${rate.toFixed(2)}</b></div>
              <div className="flex justify-between text-muted-foreground"><span>Platform fee ({(basicPct*100).toFixed(0)}%)</span>–${rateFeeBasic.toFixed(2)}</div>
              <div className="mt-1 pt-1 border-t flex justify-between font-semibold">Estimated earnings<span data-testid="basic-earn">${(rate - rateFeeBasic).toFixed(2)}</span></div>
            </div>
            <div className="rounded-xl border border-[color:hsl(var(--amber))]/40 bg-[color:hsl(var(--amber))]/5 p-3">
              <div className="text-xs font-mono-label text-[color:hsl(var(--amber))]">Pro</div>
              <div className="mt-1 flex justify-between"><span>Your rate</span><b>${rate.toFixed(2)}</b></div>
              <div className="flex justify-between text-muted-foreground"><span>Platform fee ({(proPct*100).toFixed(0)}%)</span>–${rateFeePro.toFixed(2)}</div>
              <div className="mt-1 pt-1 border-t flex justify-between font-semibold">Estimated earnings<span data-testid="pro-earn">${(rate - rateFeePro).toFixed(2)}</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PlanCard({ title, price, per, feePct, perks, highlight, action, note, testId }) {
  return (
    <div className={`border rounded-3xl p-6 ${highlight ? "border-[color:hsl(var(--amber))]/40 bg-gradient-to-b from-[color:hsl(var(--amber))]/5 to-transparent" : "bg-card"}`} data-testid={testId}>
      <div className="flex items-center gap-2">
        <h3 className="font-display font-bold text-xl">{title}</h3>
        {highlight && <span className="text-[10px] font-mono-label rounded-full bg-[color:hsl(var(--amber))]/20 text-[color:hsl(var(--amber))] px-2 py-0.5">Recommended for active helpers</span>}
      </div>
      <div className="mt-3 flex items-baseline gap-1">
        <div className="font-display font-extrabold text-3xl">{price}</div>
        <div className="text-sm text-muted-foreground">{per}</div>
      </div>
      <div className="mt-3 text-sm"><b>{(feePct*100).toFixed(0)}%</b> <span className="text-muted-foreground">Immediate Help platform fee</span></div>
      <ul className="mt-4 space-y-1.5 text-sm">
        {perks.map((p) => <li key={p} className="flex items-start gap-2"><Sparkles className="w-3 h-3 mt-1 text-[color:hsl(var(--teal))]" /> {p}</li>)}
      </ul>
      {action}
      {note && <div className="mt-3 text-[11px] text-muted-foreground">{note}</div>}
    </div>
  );
}
